import { useCallback, useMemo } from 'react';

import { useAnalyticsContext } from './context';

type TrackFn = (eventName: string, data: Record<string, unknown>) => void;

type TransactionsTracker = {
  detailOpened: (params: {
    transactionId: string;
    type?: string | null;
    mode?: string | null;
    accountId?: string | null;
    currency?: string | null;
    amount?: number | null;
  }) => void;

  createOpened: () => void;

  created: (params: {
    rowCount: number;
    accountId?: string | null;
    isEdit: boolean;
  }) => void;

  editOpened: (params: {
    transactionId: string;
    mode?: string | null;
  }) => void;

  deleted: (params: {
    transactionId: string;
    type?: string | null;
    mode?: string | null;
    amount?: number | null;
  }) => void;

  categorized: (params: {
    transactionId: string;
    categoryCount: number;
    hasSplit: boolean;
    taxesApplied?: number;
    type?: string | null;
  }) => void;

  bulkCategorized: (params: {
    selectedCount: number;
    categoryId?: string | null;
    type?: string | null;
    isUncategorizing: boolean;
  }) => void;

  reportGenerated: (params: {
    reportId?: string | number | null;
    filterCount?: number;
  }) => void;

  fileUploaded: (params: {
    fileMappingType?: string | null;
    fileName?: string | null;
    fileSizeKb?: number | null;
  }) => void;
};

export const useTransactionsTracking = (): TransactionsTracker => {
  const analytics = useAnalyticsContext();

  const track: TrackFn = useCallback((eventName, data) => {
    try {
      analytics?.mixPanel?.trackContinueEvent?.({
        customEvent: eventName,
        module: 'transactions',
        ...data,
      });
    } catch {
      // Tracking must never break the UI.
    }
  }, [analytics]);

  return useMemo<TransactionsTracker>(() => ({
    detailOpened: (params) => track('transaction_detail_opened', params),
    createOpened: () => track('transaction_create_opened', {}),
    created: (params) => track('transaction_created', params),
    editOpened: (params) => track('transaction_edit_opened', params),
    deleted: (params) => track('transaction_deleted', params),
    categorized: (params) => track('transaction_categorized', params),
    bulkCategorized: (params) => track('transactions_bulk_categorized', params),
    reportGenerated: (params) => track('transactions_report_generated', params),
    fileUploaded: (params) => track('transactions_file_uploaded', params),
  }), [track]);
};
